import {
  CountryCode,
  formatIncompletePhoneNumber,
  parseIncompletePhoneNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js/min';
import * as R from 'ramda';
import React from 'react';

/**
 * A custom hook that returns a function that parses a formatted phone number and returns an unformatted string
 *
 * This really shouldn't be necessary, but because of how the parser and formatter work with inputs,
 * you can find a situation where you can't backspace anymore because formatting across edits outputs the same
 * value.
 *
 * Example: (555). If you try to backspace to remove the right parentheses, the formatter will see the value as
 * (555, which it will then format to (555), giving the appearance that you can't backspace anymore.
 *
 * This custom hook is meant to account for that by storing and comparing the previous value and manipulating
 * the parsed number when certain conditions are met.
 */
export function usePhoneNumberParser(countryCode: CountryCode) {
  const [prevPhoneNumber, setPrevPhoneNumber] = React.useState('');

  return function parsePhoneNumber(value: string | undefined) {
    const phoneNumber = value || '';
    let parsedPhoneNumber = parseIncompletePhoneNumber(phoneNumber);

    if (parsedPhoneNumber === prevPhoneNumber) {
      const formattedPhoneNumber = formatIncompletePhoneNumber(
        parsedPhoneNumber,
        countryCode
      );

      // Ex. formattedPhoneNumber = "(555)", phoneNumber = "(555"
      if (formattedPhoneNumber.indexOf(phoneNumber) === 0) {
        // Trim off the last character. In our example, this would shave off the last "5" in order to prevent the formatter
        // from reformatting it to the same phoneNumber.
        parsedPhoneNumber = parsedPhoneNumber.slice(0, -1);
      }
    }

    setPrevPhoneNumber(parsedPhoneNumber);

    return parsedPhoneNumber;
  };
}

/**
 * Creates a function that formats a string into a valid phone number for the given country code
 */
export const makePhoneNumberFormatter =
  (countryCode: CountryCode) => (value: string) =>
    formatIncompletePhoneNumber(value, countryCode);

export function validatePhoneNumber(
  countryCode: CountryCode,
  phoneNumber: string | undefined
) {
  const phoneNumberInstance = parsePhoneNumberFromString(
    phoneNumber || '',
    countryCode
  );

  if (!phoneNumberInstance) return;

  // It's too long or it's too short
  if (!phoneNumberInstance.isPossible()) {
    return 'Please enter a phone number with the correct number of digits.';
  }

  // The phone number is the correct length, but it's not valid.
  if (!phoneNumberInstance.isValid()) {
    return 'Please enter a valid phone number.';
  }
}

export const makePhoneNumberValidator = R.curry(validatePhoneNumber);
